import type {
  PropertyValueItem,
  WorkflowTaskDetail,
} from "../services/regulationsService";
import { parseBoolean } from "../components/FormRenderer/formUtils";
import {
  buildSaveCustomPropertyMap,
  buildTaskCustomAssigneeFromFormData,
} from "./taskDetailToFormConfig";
import {
  normalizeDynamicFormTextFieldType,
  resolveDynamicFormMaxLen,
  type TaskDetailDynamicFormConfig,
} from "./taskDetailToDynamicFormConfig";
import type {
  InitTaskPayload,
  PreInitTask,
  PreInitWorkflowField,
  WorkflowStartReference,
} from "../types/manualAssessmentWorkflow";

/** Pre-init dialog display labels (field keys unchanged for API mapping). */
export const PRE_INIT_FIELD_LABELS: Readonly<Record<string, string>> = {
  taskOwners: "Assign To",
};

/** Pre-init select placeholders (field keys unchanged for API mapping). */
export const PRE_INIT_FIELD_PLACEHOLDERS: Readonly<Record<string, string>> = {
  taskOwners: "Select assignee",
  notifyAdditionalRecipients: "Select to notify",
};

/** Checkbox option label beside Email Notification (`openCommonNotifications`). */
export const PRE_INIT_EMAIL_NOTIFICATION_CHECKBOX_LABEL = "Yes";

/** Hover tooltip for the Assign To (`taskOwners`) info icon in the pre-init dialog. */
export const PRE_INIT_TASK_OWNERS_INFO_TOOLTIP =
  "Select one of below RI Coordinator as the assignee";

/** Fields excluded from the pre-init initialization form UI and payload. */
const PRE_INIT_EXCLUDED_FIELDS = new Set(["dueDate"]);

/** Fields always optional in the pre-init dialog (override backend `required`). */
const PRE_INIT_OPTIONAL_FIELDS = new Set([
  "taskOwners",
  "taskAssignee",
  "notifyAdditionalRecipients",
]);

/** First-row tail (US-01): Notify + Email Notification — 4+4+4 grid with Assign To. */
const PRE_INIT_FIRST_ROW_TAIL_FIELDS = [
  "notifyAdditionalRecipients",
  "openCommonNotifications",
] as const;

const PRE_INIT_ROW_ONE_LAYOUT = {
  field_xs: 12,
  field_md: 4,
  field_lg: 4,
  field_xl: 4,
} as const;

const PRE_INIT_FULL_ROW_LAYOUT = {
  field_xs: 12,
  field_md: 12,
  field_lg: 12,
  field_xl: 12,
} as const;

/** Defaults when backend omits asyncConfig paths (common package CustomSelectGroupWidget). */
export const PRE_INIT_TASK_OWNERS_GROUP_CONFIG = {
  isCustomGroup: true,
  groupPath: "type",
  groupLabel: {
    taskUsers: "Users",
    taskGroups: "User Groups",
    taskRoles: "Roles",
  },
} as const;

/** Pre-init cardinality: Assign To single-select; taskAssignee is a single id string in API. */
export const PRE_INIT_TASK_OWNERS_MULTIPLE = false;
export const PRE_INIT_TASK_ASSIGNEE_MULTIPLE = false;

export const PRE_INIT_TASK_OWNERS_ASYNC_CONFIG = {
  url: "/pm/selection/task-owners",
  labelPath: "name",
  subLabelPath: "email",
  valuePath: "id",
} as const;

export const PRE_INIT_TASK_ASSIGNEE_ASYNC_CONFIG = {
  url: "/pm/selection/task-assignees",
  requestMethod: "POST",
  labelPath: "name",
  subLabelPath: "email",
  valuePath: "id",
  body: [
    { store: "formState", fieldName: "taskOwners", valuePath: "taskOwners" },
  ],
  /** Omit dependencies so Notify can load before Assign To is chosen (both optional in pre-init). */
} as const;

const PRE_INIT_DEBUG_PREFIX = "[PreInit taskAssignee]";

const PRE_INIT_STATIC_DEFAULT_ASYNC_CONFIG: Readonly<
  Record<string, Record<string, unknown>>
> = {
  taskOwners: { ...PRE_INIT_TASK_OWNERS_ASYNC_CONFIG },
  taskAssignee: { ...PRE_INIT_TASK_ASSIGNEE_ASYNC_CONFIG },
  notifyAdditionalRecipients: { ...PRE_INIT_TASK_OWNERS_ASYNC_CONFIG },
};

function mergePreInitAsyncConfig(
  field: string,
  existing?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const defaults = PRE_INIT_STATIC_DEFAULT_ASYNC_CONFIG[field];
  if (!defaults && !existing) return undefined;
  const merged = { ...defaults, ...existing };
  if (field === "taskAssignee") {
    if (!Array.isArray(merged.body) || merged.body.length === 0) {
      merged.body = [...PRE_INIT_TASK_ASSIGNEE_ASYNC_CONFIG.body];
    }
    delete merged.dependencies;
  }
  return merged;
}

export function normalizePreInitPropertyItems(
  items: readonly PropertyValueItem[],
): PropertyValueItem[] {
  return items
    .filter((item) => item.field && !PRE_INIT_EXCLUDED_FIELDS.has(item.field))
    .map((item) => {
      const field = item.field as string;
      const labelOverride = PRE_INIT_FIELD_LABELS[field];
      const asyncConfig = mergePreInitAsyncConfig(
        field,
        item.asyncConfig as Record<string, unknown> | undefined,
      );

      const fieldOverrides: Partial<PropertyValueItem> = {};
      if (PRE_INIT_OPTIONAL_FIELDS.has(field)) {
        fieldOverrides.required = false;
      }
      const placeholder = PRE_INIT_FIELD_PLACEHOLDERS[field];
      if (placeholder) {
        fieldOverrides.placeholder = placeholder;
      }
      if (field === "taskOwners") {
        fieldOverrides.description = PRE_INIT_TASK_OWNERS_INFO_TOOLTIP;
        fieldOverrides.multiple = PRE_INIT_TASK_OWNERS_MULTIPLE;
        const existingGroup = (item as { groupConfig?: unknown }).groupConfig;
        fieldOverrides.groupConfig =
          existingGroup ?? PRE_INIT_TASK_OWNERS_GROUP_CONFIG;
      }

      if (field === "taskAssignee") {
        fieldOverrides.multiple = PRE_INIT_TASK_ASSIGNEE_MULTIPLE;
      }

      if (field === "notifyAdditionalRecipients") {
        fieldOverrides.multiple = true;
        const existingGroup = (item as { groupConfig?: unknown }).groupConfig;
        fieldOverrides.groupConfig =
          existingGroup ?? PRE_INIT_TASK_OWNERS_GROUP_CONFIG;
      }

      if (field === "taskAssignee" && asyncConfig) {
        console.info(PRE_INIT_DEBUG_PREFIX, "normalized asyncConfig", {
          asyncConfig,
        });
      }

      return {
        ...item,
        ...(labelOverride
          ? { label: labelOverride, originlabel: labelOverride }
          : {}),
        ...(asyncConfig ? { asyncConfig } : {}),
        ...fieldOverrides,
      };
    });
}

function isPreInitItemHidden(item: PropertyValueItem): boolean {
  return item.hide === true;
}

/** Visible Assign To uses `taskOwners`; when hidden, `taskAssignee` takes the first row slot. */
function resolvePreInitAssignToField(
  items: readonly PropertyValueItem[],
): string | null {
  const byField = new Map(
    items.map((item) => [String(item.field), item] as const),
  );
  const taskOwners = byField.get("taskOwners");
  if (taskOwners && !isPreInitItemHidden(taskOwners)) {
    return "taskOwners";
  }
  if (byField.has("taskAssignee")) {
    return "taskAssignee";
  }
  return null;
}

function getPreInitFirstRowFieldOrder(items: readonly PropertyValueItem[]): string[] {
  const byField = new Set(items.map((item) => String(item.field)));
  const order: string[] = [];
  const assignToField = resolvePreInitAssignToField(items);
  if (assignToField && byField.has(assignToField)) {
    order.push(assignToField);
  }
  for (const field of PRE_INIT_FIRST_ROW_TAIL_FIELDS) {
    if (byField.has(field)) {
      order.push(field);
    }
  }
  return order;
}

function orderPreInitDisplayItems(
  items: readonly PropertyValueItem[],
): PropertyValueItem[] {
  const byField = new Map(
    items.map((item) => [String(item.field), item] as const),
  );
  const ordered: PropertyValueItem[] = [];

  for (const field of getPreInitFirstRowFieldOrder(items)) {
    const item = byField.get(field);
    if (item) {
      ordered.push(item);
      byField.delete(field);
    }
  }

  return [...ordered, ...sortPropertyItemsByOrder([...byField.values()])];
}

/** Common Dynamic Form re-sorts by `order`; align with our display sequence. */
function assignPreInitDisplayOrder(
  items: readonly PropertyValueItem[],
): PropertyValueItem[] {
  return items.map((item, index) => ({
    ...item,
    order: index + 1,
  }));
}

function applyPreInitFieldLayouts(
  items: readonly PropertyValueItem[],
): PropertyValueItem[] {
  const firstRowFields = new Set(getPreInitFirstRowFieldOrder(items));
  return items.map((item) => ({
    ...item,
    ...(firstRowFields.has(String(item.field))
      ? PRE_INIT_ROW_ONE_LAYOUT
      : PRE_INIT_FULL_ROW_LAYOUT),
  }));
}

/** Map pre-init `workflowFields` rows to Dynamic Form property items. */
export function workflowFieldsToPropertyItems(
  fields: readonly PreInitWorkflowField[],
): PropertyValueItem[] {
  return fields
    .map((raw, index) => {
      const field = String(raw.field ?? raw.name ?? "").trim();
      if (!field || PRE_INIT_EXCLUDED_FIELDS.has(field)) return null;

      const { type, textArea } = normalizeDynamicFormTextFieldType(raw);
      const maxLen = resolveDynamicFormMaxLen(raw);
      const label =
        (typeof raw.label === "string" && raw.label.trim()) || field;
      return {
        ...raw,
        field,
        type,
        ...(textArea ? { textArea } : {}),
        ...(maxLen != null ? { maxLen } : {}),
        label,
        originlabel: label,
        hide: false,
        order: typeof raw.order === "number" ? raw.order : 1000 + index,
        readonly: raw.readonly === true,
        disabled: raw.disabled === true,
        required: parseBoolean(raw.required),
      } as PropertyValueItem;
    })
    .filter((item): item is PropertyValueItem => item != null);
}

function sortPropertyItemsByOrder(
  items: readonly PropertyValueItem[],
): PropertyValueItem[] {
  return [...items].sort((a, b) => {
    const orderA = typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return String(a.field ?? "").localeCompare(String(b.field ?? ""));
  });
}

export function syntheticTaskDetailFromInitTasks(
  initTasks: readonly PreInitTask[],
  workflowFields: readonly PreInitWorkflowField[] = [],
): WorkflowTaskDetail {
  const initTaskItems = normalizePreInitPropertyItems(
    initTasks.flatMap((task) => task.propertyValueItemList ?? []),
  );
  const workflowItems = normalizePreInitPropertyItems(
    workflowFieldsToPropertyItems(workflowFields),
  );
  const propertyValueItemList = applyPreInitFieldLayouts(
    assignPreInitDisplayOrder(
      orderPreInitDisplayItems([...initTaskItems, ...workflowItems]),
    ),
  );
  return {
    task: {
      propertyValueItemList,
    },
  };
}

/** Map workflowFields with `target: references` to POST /manual-assessment/workflow `references`. */
export function buildWorkflowReferences(
  workflowFields: readonly PreInitWorkflowField[],
  formData: Record<string, unknown>,
): WorkflowStartReference[] {
  const references: WorkflowStartReference[] = [];

  for (const raw of workflowFields) {
    if (raw.target !== "references") continue;

    const field = String(raw.field ?? raw.name ?? "").trim();
    if (!field) continue;

    const value = formData[field];
    if (value === undefined || value === null || value === "") continue;

    references.push({
      field,
      name:
        (typeof raw.label === "string" && raw.label.trim()) ||
        (typeof raw.name === "string" && raw.name.trim()) ||
        field,
      content_type:
        typeof raw.content_type === "string" && raw.content_type.trim()
          ? raw.content_type.trim()
          : "text/plain",
      value: String(value),
    });
  }

  return references;
}

function isPreInitValueEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "number" || typeof value === "boolean") return false;
  if (Array.isArray(value)) {
    return (
      value.length === 0 || value.every((entry) => isPreInitValueEmpty(entry))
    );
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if ("value" in obj || "id" in obj) {
      return isPreInitValueEmpty(obj.value ?? obj.id);
    }
    return Object.keys(obj).length === 0;
  }
  return false;
}

/** Block Initiate when a visible required pre-init field is empty. */
export function validatePreInitFormData(
  jsonData: TaskDetailDynamicFormConfig["jsonData"],
  formData: Record<string, unknown>,
): string | null {
  for (const item of jsonData) {
    if (!parseBoolean(item.required)) continue;
    if (parseBoolean(item.readonly) || parseBoolean(item.disabled)) continue;
    const field = item.field != null ? String(item.field) : "";
    if (!field) continue;
    if (!isPreInitValueEmpty(formData[field])) continue;

    const label =
      (typeof item.label === "string" && item.label.trim()) ||
      (typeof item.originlabel === "string" && item.originlabel.trim()) ||
      field;
    return `${label} is required.`;
  }
  return null;
}

/** Map pre-init dynamic form values to workflow `init_tasks` payload. */
export function buildInitTasksPayload(
  initTasks: readonly PreInitTask[],
  formData: Record<string, unknown>,
): InitTaskPayload[] {
  const taskDetail = syntheticTaskDetailFromInitTasks(initTasks, []);
  const customPropertyMap = buildSaveCustomPropertyMap(taskDetail, formData);
  const taskCustomAssignee = buildTaskCustomAssigneeFromFormData(formData);

  return initTasks
    .map((task, index) => {
      const taskDefinitionId =
        task.taskDefinitionId != null ? String(task.taskDefinitionId) : "";

      const payload: InitTaskPayload = {
        taskDefinitionId: taskDefinitionId || `init-task-${index}`,
        customPropertyMap,
      };

      if (taskCustomAssignee && index === 0) {
        payload.taskCustomAssignee = taskCustomAssignee;
      }

      return payload;
    })
    .filter((entry) => entry.taskDefinitionId);
}
